import React, {
  useState,
  useEffect,
  useCallback,
  startTransition,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  TextField,
  Divider,
  Stack,
  CardContent,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
} from '@mui/material';
import { Add, Remove, Delete, LocationOn } from '@mui/icons-material';
import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import SectionHeader from '../../components/common/SectionHeader';
import { environment } from '../../../environments/environment';
import { fetchData, handleToaster } from '../../Utils/UtilsFunctions.js';
import {
  setCart,
  setUser,
  UpdateCartData,
  UdpateNote,
  CheckItem,
  userAddresses,
  setDeliveryStore,
  LoadDeliveryFees,
} from '@tabletuck/redux-slices';
import { auth, firestore } from '@tabletuck/firebase-services';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import StorefrontIcon from '@mui/icons-material/Storefront';
import CancelOutlinedIcon from '@mui/icons-material/Cancel';
import NeedQuestion from '../../components/NeedQuestion';
import { useCartPricing } from '../../hooks/useCartPricing';
import { useCartItemActions } from '../../hooks/useCartItemActions';
import { useDeliveryAddress } from '../../hooks/useDeliveryAddress';
import { useAnonymousAuth } from '../../hooks/useAnonymousAuth';
import { useMemo } from 'react';
import { Skeleton, CircularProgress } from '@mui/material';
import BillDetails from '../../components/CartDrawer/BillDetails';
import { formatPriceFromCents } from '../../Utils/UtilsFunctions';
import { calculateDistance } from '../../services/locationService';
import DeliveryDialog from '../../components/CartDrawer/DeliveryDialog';
import FattyPattyDialog from '../../components/FattyPattyDialog';
import logo from '../../../assets/fattyBattyLogo.png';

// Separate component for cart item row to use hooks properly
const CartItemRow = ({
  lineItem,
  cart,
  index,
  onItemRemoved,
  onItemUpdated,
  onEditItem,
}) => {
  // Find matching cart item for additional details (like image)
  const matchingCartItem = useMemo(() => {
    if (!Array.isArray(cart) || cart.length === 0) return null;
    return cart.find((cartItem) => {
      return (
        cartItem.itemId === lineItem.variationId ||
        cartItem.title?.toLowerCase() === lineItem.name?.toLowerCase() ||
        cartItem.variationId === lineItem.variationId
      );
    });
  }, [cart, lineItem]);

  // Transform lineItem data to match Cart page UI needs
  const transformedItem = useMemo(() => {
    return {
      // Include original lineItem fields first
      ...lineItem,
      itemId:
        lineItem.variationId ||
        matchingCartItem?.itemId ||
        `line-item-${index}`,
      title: lineItem.name || matchingCartItem?.title || 'Unknown Item',
      quantity: parseInt(lineItem.quantity) || 1,
      image: matchingCartItem?.image || '',
      modifierDetails:
        lineItem.modifiers?.map((modifier) => ({
          optionTitle: modifier.name || 'Unknown Modifier',
          price: modifier.totalPriceMoney?.amount
            ? parseFloat(modifier.totalPriceMoney.amount) / 100
            : 0,
          quantity: parseInt(modifier.quantity) || 1,
        })) ||
        matchingCartItem?.modifierDetails ||
        [],
      variationId: lineItem.variationId || matchingCartItem?.variationId,
      variationName:
        lineItem.variationName || matchingCartItem?.variationName || '',
      // Unit price from basePriceMoney (amount in cents)
      price:
        lineItem.basePriceMoney?.amount != null
          ? parseFloat(lineItem.basePriceMoney.amount) / 100
          : lineItem.grossSalesMoney
          ? parseFloat(lineItem.grossSalesMoney) /
            100 /
            (parseInt(lineItem.quantity) || 1)
          : matchingCartItem?.price ?? 0,
      // Line total: (basePriceMoney * quantity) + modifier totals
      calculatedPrice: (() => {
        if (lineItem.basePriceMoney?.amount != null) {
          const unit = parseFloat(lineItem.basePriceMoney.amount) / 100;
          const qty = parseInt(lineItem.quantity) || 1;
          const modifierTotal = (lineItem.modifiers || []).reduce(
            (sum, m) => sum + parseFloat(m?.totalPriceMoney?.amount || 0) / 100,
            0
          );
          return unit * qty + modifierTotal;
        }
        if (lineItem.grossSalesMoney)
          return parseFloat(lineItem.grossSalesMoney) / 100;
        return matchingCartItem?.calculatedPrice ?? 0;
      })(),
    };
  }, [lineItem, matchingCartItem, index]);

  // Use the cart item actions hook for this item
  const {
    quantity,
    loading: itemLoading,
    isUpdatingPrice,
    handleQuantityChange,
    handleItemRemove,
  } = useCartItemActions(transformedItem, {
    maxQuantity: 10,
    onUpdate: onItemUpdated, // Callback to refresh pricing when quantity changes
    onRemove: onItemRemoved, // Callback to refresh pricing when item is removed
  });

  // Use live quantity for total when we have basePriceMoney, so the first
  // quantity change shows the correct total before apiPricingData/lineItem updates.
  const displayTotal = useMemo(() => {
    if (lineItem?.basePriceMoney?.amount != null) {
      const unit = parseFloat(lineItem.basePriceMoney.amount) / 100;
      const modTotal = (lineItem.modifiers || []).reduce(
        (s, m) => s + parseFloat(m?.totalPriceMoney?.amount || 0) / 100,
        0
      );
      return unit * quantity + modTotal;
    }
    return transformedItem.calculatedPrice;
  }, [
    lineItem?.basePriceMoney?.amount,
    lineItem?.modifiers,
    quantity,
    transformedItem.calculatedPrice,
  ]);

  return (
    <TableRow
      key={`${transformedItem.itemId}-${index}`}
      sx={{
        '&:hover': { backgroundColor: '#F8F9FA' },
        borderBottom: '1px solid #E0E0E0',
      }}
    >
      <TableCell sx={{ padding: { xs: '10px 6px', md: '16px' } }}>
        <Box
          onClick={() => onEditItem?.(transformedItem, matchingCartItem)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1, md: 2 },
            cursor: 'pointer',
          }}
        >
          <Box
            sx={{
              width: { xs: '40px', md: '64px' },
              height: { xs: '40px', md: '64px' },
              flexShrink: 0,
              borderRadius: '50%',
              backgroundColor: '#F2F2F2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            {transformedItem.image ? (
              <img
                src={transformedItem.image}
                alt={transformedItem.title}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            ) : (
              <img
                src={logo}
                alt="Default Item Logo"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  padding: '8px',
                }}
              />
            )}
          </Box>
          <Box>
            <Typography
              variant="body1"
              sx={{
                fontFamily: 'Jost, sans-serif',
                fontWeight: 700,
                fontSize: { xs: '13px', md: '18px' },
                textTransform: 'capitalize',
                color: '#172B4D',
                marginBottom:
                  transformedItem?.modifierDetails?.length > 0 ? '4px' : 0,
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'baseline',
                gap: 0.5,
              }}
            >
              {transformedItem.title}
              {(lineItem.variationName || transformedItem.variationName) && (
                <Box
                  component="span"
                  sx={{
                    fontSize: { xs: '11px', md: '15px' },
                    color: '#5C6B7A',
                    fontFamily: 'Jost, sans-serif',
                    fontWeight: 500,
                  }}
                >
                  {` (${
                    lineItem.variationName || transformedItem.variationName
                  })`}
                </Box>
              )}
            </Typography>

            {/* Display modifiers if they exist */}
            {transformedItem?.modifierDetails &&
              transformedItem.modifierDetails.length > 0 && (
                <Box sx={{ marginBottom: { xs: '4px', md: '8px' } }}>
                  {transformedItem.modifierDetails.map((modifier, modIndex) => (
                    <Typography
                      key={modIndex}
                      variant="body2"
                      sx={{
                        fontSize: { xs: '10px', md: '13px' },
                        color: '#666',
                        fontFamily: 'Jost, sans-serif',
                        textTransform: 'capitalize',
                        lineHeight: 1.3,
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: '2px',
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          width: '4px',
                          height: '4px',
                          borderRadius: '50%',
                          backgroundColor: '#666',
                          marginRight: '8px',
                          flexShrink: 0,
                        }}
                      />
                      {modifier.quantity && modifier.quantity > 1 && (
                        <Box
                          component="span"
                          sx={{
                            color: '#172B4D',
                            fontWeight: '600',
                            marginRight: '4px',
                            fontSize: '12px',
                          }}
                        >
                          {modifier.quantity}x
                        </Box>
                      )}
                      {modifier.optionTitle}
                      {modifier.price > 0 && (
                        <Box
                          component="span"
                          sx={{
                            color: '#2C742F',
                            fontWeight: '600',
                            marginLeft: '6px',
                            fontSize: '12px',
                          }}
                        >
                          (+${(modifier.price || 0).toFixed(2)})
                        </Box>
                      )}
                    </Typography>
                  ))}
                </Box>
              )}
          </Box>
        </Box>
      </TableCell>
      <TableCell align="center" sx={{ padding: { xs: '10px 4px', md: '16px' } }}>
        {isUpdatingPrice ? (
          <Skeleton
            variant="text"
            width={60}
            height={24}
            sx={{ margin: '0 auto' }}
          />
        ) : (
          <Typography
            variant="body1"
            sx={{
              fontFamily: 'Jost, sans-serif',
              fontWeight: 600,
              fontSize: { xs: '12px', md: '16px' },
              color: '#1A1A1A',
            }}
          >
            ${transformedItem.price?.toFixed(2) || '0.00'}
          </Typography>
        )}
      </TableCell>
      <TableCell align="center" sx={{ padding: { xs: '10px 4px', md: '16px' } }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: { xs: 0.5, md: 1 },
            width: 'fit-content',
            border: '1px solid #E6E6E6',
            margin: '0 auto',
            borderRadius: '170px',
            padding: { xs: '4px', md: '8px' },
          }}
        >
          <IconButton
            size="small"
            onClick={() => {
              if (quantity > 1) {
                handleQuantityChange('dec', transformedItem);
              }
            }}
            disabled={quantity <= 1 || itemLoading}
            sx={{
              backgroundColor: '#F2F2F2',
              '&:hover': { backgroundColor: '#F2F2F2' },
              width: { xs: 22, md: 32 },
              height: { xs: 22, md: 32 },
            }}
          >
            <Remove sx={{ fontSize: { xs: '14px', md: '20px' } }} />
          </IconButton>
          <Typography
            sx={{
              minWidth: { xs: 18, md: 40 },
              textAlign: 'center',
              fontFamily: 'Jost, sans-serif',
              fontWeight: 600,
              fontSize: { xs: '12px', md: '16px' },
            }}
          >
            {quantity}
          </Typography>
          <IconButton
            size="small"
            onClick={() => {
              if (quantity < 10) {
                handleQuantityChange('inc', transformedItem);
              }
            }}
            disabled={quantity >= 10 || itemLoading}
            sx={{
              backgroundColor: '#F2F2F2',
              color: '#1A1A1A',
              '&:hover': {
                backgroundColor: '#F2F2F2',
                opacity: 0.8,
              },
              width: { xs: 22, md: 32 },
              height: { xs: 22, md: 32 },
            }}
          >
            <Add sx={{ fontSize: { xs: '14px', md: '20px' } }} />
          </IconButton>
        </Box>
      </TableCell>
      <TableCell align="center" sx={{ padding: { xs: '10px 4px', md: '16px' } }}>
        {isUpdatingPrice ? (
          <Skeleton
            variant="text"
            width={80}
            height={24}
            sx={{ margin: '0 auto' }}
          />
        ) : (
          <Typography
            sx={{
              fontFamily: 'Jost, sans-serif',
              fontWeight: 600,
              fontSize: { xs: '12px', md: '16px' },
              color: '#1A1A1A',
            }}
          >
            ${displayTotal?.toFixed(2) || '0.00'}
          </Typography>
        )}
      </TableCell>
      <TableCell align="center" sx={{ padding: { xs: '10px 4px', md: '16px' } }}>
        <IconButton
          onClick={() => handleItemRemove(transformedItem)}
          disabled={itemLoading}
          sx={{ padding: { xs: '4px', md: '8px' } }}
        >
          {itemLoading ? (
            <CircularProgress size={16} />
          ) : (
            <CancelOutlinedIcon sx={{ fontSize: { xs: '18px', md: '24px' } }} />
          )}
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

const Cart = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const cart = useSelector((state) => state.shopReducer.cart);
  const menuItems = useSelector((state) => state.shopReducer.currentItem);
  const modifiers = useSelector((state) => state.shopReducer.modifiers);
  const user = useSelector((state) => state.authReducer.userData);
  const note = useSelector((state) => state.shopReducer.note);
  const [localNote, setLocalNote] = useState(() => note || '');
  const isEditingNoteRef = useRef(false);
  const noteDebounceRef = useRef(null);
  const cartData = useSelector((state) => state.shopReducer.cartData);
  const { scheduledTime: cartDataScheduledTime } = cartData;
  const reduxScheduledTime = useSelector(
    (state) => state.shopReducer.scheduledTime
  );
  const stores = useSelector((state) => state.shopReducer.stores);
  const addresses = useSelector((state) => state.authReducer.userAddresses);
  const deliveryStore = useSelector((state) => state.shopReducer.deliveryStore);
  const orderTypeLocked = useSelector((state) => state.shopReducer.orderTypeLocked);
  const { code, discount, isVerify, scheduledTime } = cartData;
  const [t] = useTranslation();

  useEffect(() => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      return;
    }

    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isMobileViewport = window.matchMedia('(max-width: 900px)').matches;

    if (!isIOS || !isMobileViewport) {
      return;
    }

    const refreshKey = 'cart_ios_mobile_refresh_once';
    const hasRefreshed = sessionStorage.getItem(refreshKey) === '1';

    if (hasRefreshed) {
      // Clear the flag so the next cart visit can refresh once again.
      sessionStorage.removeItem(refreshKey);
      return;
    }

    sessionStorage.setItem(refreshKey, '1');
    window.location.reload();
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const active = document.activeElement;
    if (active && typeof active.blur === 'function') {
      active.blur();
    }

    const body = document.body;
    if (body?.style?.overflow === 'hidden') {
      body.style.overflow = '';
    }
    if (body?.style?.paddingRight) {
      body.style.paddingRight = '';
    }

    const html = document.documentElement;
    if (html?.style?.overflow === 'hidden') {
      html.style.overflow = '';
    }
  }, []);

  // Keep local note synced with Redux when not editing (prevents cursor jumps)
  useEffect(() => {
    if (isEditingNoteRef.current) return;
    setLocalNote(note || '');
  }, [note]);

  // Debounce Redux updates for iOS keyboard performance
  useEffect(() => {
    if (isEditingNoteRef.current !== true) return;
    if (noteDebounceRef.current) clearTimeout(noteDebounceRef.current);
    noteDebounceRef.current = setTimeout(() => {
      dispatch(UdpateNote(localNote));
    }, 350);

    return () => {
      if (noteDebounceRef.current) clearTimeout(noteDebounceRef.current);
    };
  }, [localNote, dispatch]);

  const [orderType, setOrderType] = useState(() => {
    const preferred = user?.pickupOrDelivery;
    return preferred === 'delivery' || preferred === 'pickup'
      ? preferred
      : 'pickup';
  });
  const [open, setOpen] = useState(false);
  const [forceDeliveryDialog, setForceDeliveryDialog] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(
    '4651 S 27th St, Greenfield, WI 53221, USA'
  );
  const [err, setErr] = useState('');
  const [openSuccess, setOpenSuccess] = useState(false);

  // Additional state variables to match CartDrawer
  const [enablePickup, setEnablePickup] = useState(false);
  const [enableDelivery, setEnableDelivery] = useState(false);
  const [defaultAdd, setDefaultAdd] = useState('');
  const [storeaddress, setStoreaddress] = useState('');
  const [apiError, setApiError] = useState('');
  const [isSwitchingType, setIsSwitchingType] = useState(false);
  const [typeAddress, setTypeAddress] = useState('');
  const [data, setData] = useState([]);
  const [dataStore, setDataStore] = useState([]);
  const [store, setStore] = useState('');
  const [storeId, setStoreId] = useState('');
  const [addressStore, setAddressStore] = useState('');
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [checkoutErr, setCheckoutErr] = useState('');
  const [checkoutErrTotal, setcheckoutErrTotal] = useState('');
  const [errDelivery, setErrDelivery] = useState('');
  const [deliveryTooFarDialog, setDeliveryTooFarDialog] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editDialogItem, setEditDialogItem] = useState(null);
  const [editCartItemPayload, setEditCartItemPayload] = useState(null);
  const types = ['delivery', 'pickup'];
  const typeToShow = [t('confirmPage.Delivery'), `${t('confirmPage.pickUp')}`];

  // Anonymous auth support
  const { isAnonymous } = useAnonymousAuth();

  // Get delivery address ID using the unified hook
  const {
    currentDeliveryAddressId: unifiedDeliveryAddressId,
    currentDeliveryAddressData,
    setDeliveryAddressById,
    refreshAddresses,
  } = useDeliveryAddress();

  // Get cartStoreId from cart items
  const cartStoreId = useMemo(() => {
    if (Array.isArray(cart) && cart.length > 0) {
      return cart[0]?.storeId ?? cart[0]?.store_id ?? '';
    }
    return '';
  }, [cart]);

  // Use the shared cart pricing hook
  const {
    apiPricingData,
    isCalculatingPrice,
    cartLoading,
    callCalculateSquareItemsPriceImmediate,
  } = useCartPricing({
    cart,
    cartStoreId,
    orderType,
    deliveryAddressId: unifiedDeliveryAddressId,
    enabled: auth?.currentUser?.uid && cart?.length > 0,
  });

  // Memoize filtered line items to prevent unnecessary re-renders
  const filteredLineItems = useMemo(() => {
    if (!apiPricingData?.lineItemData || !cart || cart.length === 0) {
      return [];
    }
    // Filter API line items to only show items that are still in Redux cart
    // This ensures removed items disappear immediately, even before API responds
    return apiPricingData.lineItemData.filter((lineItem) => {
      return cart.some(
        (cartItem) =>
          cartItem.itemId === lineItem.variationId ||
          cartItem.variationId === lineItem.variationId ||
          cartItem.title?.toLowerCase() === lineItem.name?.toLowerCase()
      );
    });
  }, [apiPricingData?.lineItemData, cart]);
  // Calculate display values (same as CartDrawer)
  const displaySubtotal = useMemo(() => {
    if (apiPricingData && apiPricingData.subtotal !== undefined) {
      return formatPriceFromCents(apiPricingData.subtotal);
    }
    return '0.00';
  }, [apiPricingData]);

  const displayTotal = useMemo(() => {
    if (apiPricingData && apiPricingData.total !== undefined) {
      return formatPriceFromCents(apiPricingData.total);
    }
    return '0.00';
  }, [apiPricingData]);

  const displayTax = useMemo(() => {
    if (apiPricingData && apiPricingData.tax !== undefined) {
      return formatPriceFromCents(apiPricingData.tax);
    }
    return '0.00';
  }, [apiPricingData]);

  const displayModifiersPrice = useMemo(() => {
    if (apiPricingData && apiPricingData.total_modifiers_price !== undefined) {
      return formatPriceFromCents(apiPricingData.total_modifiers_price);
    }
    return '0.00';
  }, [apiPricingData]);

  const displayTotalDiscounts = useMemo(() => {
    if (apiPricingData && apiPricingData.total_discounts !== undefined) {
      return formatPriceFromCents(apiPricingData.total_discounts);
    }
    return '0.00';
  }, [apiPricingData]);

  const displayServiceCharge = useMemo(() => {
    if (apiPricingData && apiPricingData.serviceChargeMoney !== undefined) {
      return formatPriceFromCents(
        apiPricingData.serviceChargeMoney - (apiPricingData.onlineFee || 0)
      );
    }
    return '0.00';
  }, [apiPricingData]);

  const displayOnlineFee = useMemo(() => {
    if (apiPricingData && apiPricingData.onlineFee !== undefined) {
      return formatPriceFromCents(apiPricingData.onlineFee);
    }
    return '0.00';
  }, [apiPricingData]);

  // Store data state (will be set by getUser function)
  const [storeData, setStoreData] = useState(null);

  // Get address data and addressStore
  const addressData = useMemo(() => {
    if (!unifiedDeliveryAddressId) {
      return null;
    }
    // First try to find in addresses array
    if (addresses && addresses.length > 0) {
      const found = addresses.find(
        (addr) => addr.id === unifiedDeliveryAddressId
      );
      if (found) {
        return found;
      }
    }
    // Check if deliveryStore has the full data
    const storeAddressData =
      typeof deliveryStore === 'object' ? deliveryStore?.addressData : null;
    if (storeAddressData) {
      return {
        id: unifiedDeliveryAddressId,
        ...storeAddressData,
      };
    }

    return null;
  }, [unifiedDeliveryAddressId, addresses, deliveryStore]);

  // Get store data for BillDetails (same as CartDrawer - data should be store object, not address)
  const dataForBillDetails = useMemo(() => {
    // For delivery type, find the store that matches addressStore
    if (orderType === 'delivery' && addressStore && stores) {
      const matchingStore = stores.find((store) => store.id === addressStore);
      if (matchingStore) return matchingStore;
    }
    // For pickup, use storeData
    if (orderType === 'pickup' && storeData) {
      return storeData;
    }
    // Fallback to storeData if available
    return storeData;
  }, [orderType, addressStore, stores, storeData]);

  // addressStore is now a state variable, not a useMemo

  // Calculate fallback total for BillDetails
  const total = useMemo(() => {
    if (apiPricingData && apiPricingData.total !== undefined) {
      return parseFloat(formatPriceFromCents(apiPricingData.total));
    }
    return 0;
  }, [apiPricingData]);

  // Helper to format store address into a US-style single-line string
  const formatAddress = (address) => {
    if (!address) return '';
    const line1 =
      address.addressLine1 || address.address_line1 || address.address1 || '';
    const line2 =
      address.addressLine2 || address.address_line2 || address.address2 || '';
    const city = address.locality || address.city || '';
    const state = address.administrativeDistrictLevel1 || address.state || '';
    const postal =
      address.postalCode ||
      address.zipCode ||
      address.postal_code ||
      address.zip ||
      '';

    const parts = [];
    if (line1) parts.push(line1);
    if (line2) parts.push(line2);

    // Combine city, state and postal into one part
    const cityState = [city, state].filter(Boolean).join(', ');
    const cityStatePostal = [cityState, postal].filter(Boolean).join(' ');
    if (cityStatePostal) parts.push(cityStatePostal);

    return parts.join(', ');
  };

  const getUser = async (token) => {
    try {
      const userData = user;
      const bags = userData?.bagItems;
      bags?.length && dispatch(setCart(bags));
      const storeId = cartStoreId;
      setStoreId(cartStoreId);

      stores?.map((storeItem) => {
        if (storeItem?.id === storeId) {
          setStore(storeItem?.businessName);
          setStoreData(storeItem);

          setEnablePickup(storeItem?.pickup_enabled);
          setEnableDelivery(storeItem?.delivery_enabled);
          setStoreaddress(formatAddress(storeItem?.address));
        }
      });
    } catch (e) {
      // Error in getUser
    }
  };

  const getUserAddresses = async (token) => {
    const bags = user?.bagItems;
    setStoreId(cartStoreId);
    bags?.length && dispatch(setCart(bags));

    try {
      // Fetch addresses from the addresses subcollection
      const addressesRef = firestore
        .collection('users')
        .doc(user?.uid)
        .collection('addresses');

      const addressesSnapshot = await addressesRef.get();
      const firestoreAddresses = [];

      addressesSnapshot.forEach((doc) => {
        firestoreAddresses.push({ id: doc.id, ...doc.data() });
      });

      // IMPORTANT: Preserve temporary addresses (from ChangeBranchMenu) when merging
      // These are addresses that exist in Redux but not in Firestore (for anonymous users)
      const existingTempAddresses = (addresses || []).filter(
        (a) => a.isTemporary
      );

      // Merge: Firestore addresses + temporary addresses
      const mergedAddresses = [...firestoreAddresses, ...existingTempAddresses];

      // Update Redux with the MERGED addresses (preserving temp addresses)
      dispatch(userAddresses(mergedAddresses));

      // IMPORTANT: Check if there's already a branchMenu source set
      // If so, don't overwrite the delivery address - branchMenu has priority
      const currentSource =
        typeof deliveryStore === 'object' ? deliveryStore?.source : null;
      const hasBranchMenuPriority = currentSource === 'branchMenu';

      if (firestoreAddresses.length > 0) {
        // Get the first Firestore address as default (not temp addresses)
        const firstAddress = firestoreAddresses[0];
        const defaultDeliveryAddress =
          user?.defaultDeliveryAddress || firstAddress.id;

        // Find the default address or use the first one (from Firestore only)
        const selectedAddress =
          firestoreAddresses.find((a) => a.id === defaultDeliveryAddress) ||
          firstAddress;

        // Update user document with defaultDeliveryAddress if not set
        if (!user?.defaultDeliveryAddress) {
          await firestore
            .collection('users')
            .doc(user?.uid)
            .update({ defaultDeliveryAddress: firstAddress.id });
        }

        // ONLY set delivery store if there's NO branchMenu priority
        // This preserves the address selected in ChangeBranchMenu
        if (!hasBranchMenuPriority) {
          setAddressStore(selectedAddress?.delivery_store_id || cartStoreId);
          dispatch(
            setDeliveryStore({
              address: selectedAddress.id,
              store: selectedAddress?.delivery_store_id || cartStoreId,
            })
          );

          // Set address display info
          setDefaultAdd(
            selectedAddress?.displayName || selectedAddress?.address
          );
          setTypeAddress(selectedAddress?.nickName || 'Default Address');
        } else {
          // Don't overwrite - the sync effect will handle displaying the correct address
        }

        setApiError('');

        // Reset delivery fee - will be calculated by calcPrice API
        setDeliveryFee(0);
        dispatch(LoadDeliveryFees(0));
      } else {
        // No addresses found
        setDefaultAdd('');
        setAddressStore('');
        setTypeAddress('');
        setApiError('');
        setDeliveryFee(0);
        dispatch(LoadDeliveryFees(0));
      }
    } catch (error) {
      setApiError(error?.message || 'Failed to load addresses');
      setDefaultAdd('');
      setDeliveryFee(0);
      dispatch(LoadDeliveryFees(0));
    }
  };

  // Handle order type change (same logic as CartDrawer)
  const handleChangeType = useCallback(
    async (value) => {
      // Prevent duplicate clicks on the same type
      if (value === orderType) return;

      // Check if we have a branchMenu address available (from ChangeBranchMenu)
      const hasBranchMenuAddress =
        currentDeliveryAddressData && unifiedDeliveryAddressId;
      const hasAnyAddress =
        hasBranchMenuAddress ||
        (addresses && addresses.length > 0) ||
        defaultAdd;

      // Check if user is trying to switch to delivery without an address
      if (value === 'delivery' && !hasAnyAddress) {
        handleToaster('error', 'You need to add a delivery address first');
        // Force the delivery dialog to open without changing the type
        setForceDeliveryDialog(true);
        handleClickOpen(); // Open the address modal
        return;
      }

      // Check 5-mile delivery radius restriction
      if (
        value === 'delivery' &&
        currentDeliveryAddressData?.lat &&
        currentDeliveryAddressData?.lng
      ) {
        const storeCoords =
          storeData?._geoloc ||
          storeData?.coordinates ||
          storeData?.address;
        if (storeCoords?.lat && storeCoords?.lng) {
          const distanceKm = calculateDistance(storeCoords, currentDeliveryAddressData);
          const distanceMiles = distanceKm !== null ? distanceKm * 0.621371 : null;
          if (distanceKm !== null && distanceMiles > 5) {
            setDeliveryTooFarDialog(true);
            return;
          }
        }
      }

      // If switching to delivery and we have a branchMenu address, use it
      if (value === 'delivery' && hasBranchMenuAddress && !defaultAdd) {
        setDefaultAdd(
          currentDeliveryAddressData?.displayName ||
            currentDeliveryAddressData?.address ||
            ''
        );
        setTypeAddress(
          currentDeliveryAddressData?.nickName || 'Default Address'
        );
        setAddressStore(
          currentDeliveryAddressData?.delivery_store_id || cartStoreId
        );
      }

      // IMMEDIATE UI UPDATE - update type state immediately for instant feedback
      setOrderType(value);

      // Clear errors immediately
      setCheckoutErr('');

      // Show type switching loader immediately
      setIsSwitchingType(true);

      // Call API immediately (not in startTransition) so loading state shows immediately
      // Pass the deliveryId explicitly when switching to delivery
      const deliveryId =
        value === 'delivery' ? unifiedDeliveryAddressId || '' : '';
      callCalculateSquareItemsPriceImmediate(value, deliveryId).then(() => {
        setIsSwitchingType(false);
      });

      // Defer Redux updates to prevent blocking the UI
      startTransition(() => {
        dispatch(CheckItem(''));
        const updatedUser = { ...user, pickupOrDelivery: value };
        dispatch(setUser({ userData: updatedUser }));
      });
      // Firebase update in complete background - don't wait
      if (auth?.currentUser?.uid) {
        setTimeout(() => {
          firestore
            .collection('users')
            .doc(auth.currentUser.uid)
            .set({ pickupOrDelivery: value }, { merge: true })
            .catch(() => {
              // Firebase update failed
            });
        }, 0);
      }
    },
    [
      orderType,
      addresses,
      defaultAdd,
      handleToaster,
      dispatch,
      user,
      callCalculateSquareItemsPriceImmediate,
      currentDeliveryAddressData,
      unifiedDeliveryAddressId,
      cartStoreId,
      storeData,
    ]
  );

  const handleClickOpen = () => {
    setOpen(true);
    setCheckoutErr('');
  };

  const handleClose = () => {
    setOpen(false);
  };

  // Handle when a new address is added (from force delivery flow)
  // Now uses the unified useDeliveryAddress hook for consistent state management
  // Uses source: 'deliveryDialog' which has lower priority than 'branchMenu'
  const handleNewAddressAdded = useCallback(
    async (addressId) => {
      try {
        // Refresh addresses to get the newly added one
        await refreshAddresses();

        // Use the unified hook to set this as the delivery address
        // source: 'deliveryDialog' - lower priority than branchMenu
        const storeId = cartStoreId || '';
        await setDeliveryAddressById(addressId, storeId, 'deliveryDialog');

        // Find the newly added address for local state updates
        const addressesRef = firestore
          .collection('users')
          .doc(auth?.currentUser?.uid)
          .collection('addresses');
        const addressDoc = await addressesRef.doc(addressId).get();

        if (addressDoc.exists) {
          const newAddress = { id: addressDoc.id, ...addressDoc.data() };

          // Update local state with the new address
          setDefaultAdd(newAddress?.displayName || newAddress?.address);
          setTypeAddress(newAddress?.nickName || 'Default Address');
          setAddressStore(newAddress?.delivery_store_id || cartStoreId);

          // Update user pickupOrDelivery to delivery
          const updatedUser = {
            ...user,
            defaultDeliveryAddress: addressId,
            pickupOrDelivery: 'delivery',
          };
          dispatch(setUser({ userData: updatedUser }));

          // Persist to Firestore
          if (auth?.currentUser?.uid) {
            await firestore
              .collection('users')
              .doc(auth.currentUser.uid)
              .update({
                defaultDeliveryAddress: addressId,
                pickupOrDelivery: 'delivery',
              });
          }
        }

        // Ensure delivery type is selected
        setOrderType('delivery');

        // Clear force delivery dialog flag and close the dialog
        setForceDeliveryDialog(false);
        setOpen(false);

        // Clear any errors
        setCheckoutErr('');
        setApiError('');

        // Call the pricing API with delivery type and the new addressId
        setTimeout(() => {
          callCalculateSquareItemsPriceImmediate('delivery', addressId);
        }, 100);
      } catch (error) {
        // Error handling new address
      }
    },
    [
      user,
      dispatch,
      cartStoreId,
      callCalculateSquareItemsPriceImmediate,
      refreshAddresses,
      setDeliveryAddressById,
    ]
  );

  // Function to change selected delivery address
  // Now uses the unified useDeliveryAddress hook for consistent state management
  // Uses source: 'deliveryDialog' which has lower priority than 'branchMenu'
  const handleChangeAddress = useCallback(
    async (addressId) => {
      try {
        // Find the selected address from current addresses
        const selectedAddress = addresses?.find((a) => a.id === addressId);

        if (selectedAddress) {
          // Use the unified hook to set this as the delivery address
          // source: 'deliveryDialog' - lower priority than branchMenu
          const storeId = selectedAddress?.delivery_store_id || cartStoreId;
          await setDeliveryAddressById(addressId, storeId, 'deliveryDialog');

          // Switch to delivery tab
          setOrderType('delivery');
          const updatedUser = {
            ...user,
            defaultDeliveryAddress: addressId,
            pickupOrDelivery: 'delivery',
          };
          dispatch(setUser({ userData: updatedUser }));
          if (auth?.currentUser?.uid) {
            firestore
              .collection('users')
              .doc(auth.currentUser.uid)
              .set({ pickupOrDelivery: 'delivery' }, { merge: true })
              .catch(() => {});
          }

          // Update local state
          setAddressStore(storeId);
          setDefaultAdd(
            selectedAddress?.displayName || selectedAddress?.address
          );
          setTypeAddress(selectedAddress?.nickName || 'Default Address');
          setApiError('');

          // Reset delivery fee - will be calculated by calcPrice API
          setDeliveryFee(0);
          dispatch(LoadDeliveryFees(0));

          // Close dialog
          setOpen(false);

          // Recalculate pricing with new address
          setTimeout(() => {
            callCalculateSquareItemsPriceImmediate('delivery', addressId);
          }, 100);
        }
      } catch (error) {
        setApiError('Failed to change address');
      }
    },
    [
      addresses,
      cartStoreId,
      dispatch,
      setDeliveryAddressById,
      callCalculateSquareItemsPriceImmediate,
      user,
    ]
  );

  // Handler to navigate back to menu while preserving state (store, order type, scheduled time)
  const handleBackToMenu = useCallback(() => {
    // Get current store data from stores array
    const currentStore = stores?.find((s) => s.id === cartStoreId);

    console.log('\n🔙🔙🔙 [Cart] === NAVIGATING BACK TO MENU ===');
    console.log('🔙 [Cart] Cart Items:', cart);
    console.log('🔙 [Cart] Cart Items Count:', cart?.length || 0);
    console.log('🔙 [Cart] Order Type:', orderType);
    console.log('🔙 [Cart] Cart Store ID:', cartStoreId);
    console.log('🔙 [Cart] Current Store:', currentStore);
    console.log(
      "🔙 [Cart] Cart's Original Scheduled Time (from cartData):",
      cartDataScheduledTime
    );
    console.log(
      '🔙 [Cart] Scheduled Time Store ID:',
      cartDataScheduledTime?.storeId
    );
    console.log('🔙 [Cart] Navigation State:', {
      store: currentStore || null,
      preserveOrderType: orderType,
      preserveScheduledTime: cartDataScheduledTime || null,
      fromCart: true,
      storeId: cartStoreId,
    });
    console.log('🔙🔙🔙 [Cart] === END NAVIGATION DATA ===\n');

    // Navigate to menu with state to preserve context
    navigate('/menu', {
      state: {
        store: currentStore || null,
        preserveOrderType: orderType,
        preserveScheduledTime: cartDataScheduledTime || null,
        fromCart: true,
        storeId: cartStoreId,
      },
    });
  }, [cartStoreId, orderType, cartDataScheduledTime, stores, cart, navigate]);

  const handleEditCartItem = useCallback(
    (itemFromRow, matchingCartItem) => {
      const cartIndex = Array.isArray(cart)
        ? cart.findIndex((entry) => entry === matchingCartItem)
        : -1;
      const menuItemId = matchingCartItem?.itemId || itemFromRow?.itemId || '';
      const itemToEdit = Array.isArray(menuItems)
        ? menuItems.find((menuItem) => menuItem?.id === menuItemId)
        : null;

      if (!itemToEdit) {
        handleToaster('error', 'Unable to load item details for editing');
        return;
      }

      setEditCartItemPayload({
        itemId: menuItemId,
        variationId: itemFromRow?.variationId || null,
        quantity: itemFromRow?.quantity || 1,
        customization:
          matchingCartItem?.customization ||
          itemFromRow?.customization ||
          itemFromRow?.modifiers ||
          {},
        cartIndex,
      });
      setEditDialogItem(itemToEdit);
      setEditDialogOpen(true);
    },
    [cart, menuItems]
  );

  // Optimized navigation handler for better performance (same as CartDrawer)
  const handleCheckout = useCallback(async () => {
    const hasErrors =
      (orderType === 'delivery' && apiError !== '') ||
      checkoutErr !== '' ||
      checkoutErrTotal !== '' ||
      errDelivery !== '';

    if (hasErrors) {
      return;
    }

    // Call calculateSquareItemsPrice API with the current delivery/pickup selection
    if (auth?.currentUser?.uid) {
      try {
        const token = await auth?.currentUser?.getIdToken();
        const headers = {
          'Content-Type': 'application/json',
          Authorization: token,
        };
        const requestBody = {
          deliveryId:
            orderType === 'delivery' ? unifiedDeliveryAddressId || '' : '',
          storeId: cartStoreId || '',
          deliveryOrPickup: orderType || 'pickup',
          restaurantId: environment.restaurantId,
          tipAmount: 0,
          uid: auth?.currentUser?.uid,
        };
        await fetchData(
          `${environment?.apiUrl}/calculateSquareItemsPrice?restaurantId=${environment.restaurantId}`,
          'POST',
          requestBody,
          headers
        );
      } catch (apiError) {
        // Continue with navigation even if API call fails
      }
    }

    if (
      environment.restaurantId === 'fattyPattyDev' ||
      environment.restaurantId === 'fattyPatty'
    ) {
      // Always allow navigation to confirm order page - auth check will be in ConfirmOrderPage
      if (apiPricingData) {
        // Determine final scheduled time:
        // 1. If cartData already has scheduledTime for this cart's store, keep it
        // 2. Else if Redux scheduledTime matches cart's store, use it
        // 3. Otherwise, clear it (null)
        let finalScheduledTime = null;

        if (
          cartDataScheduledTime &&
          cartDataScheduledTime.storeId === cartStoreId
        ) {
          // Keep existing cart's scheduled time
          finalScheduledTime = cartDataScheduledTime;
        } else if (
          reduxScheduledTime &&
          reduxScheduledTime.storeId === cartStoreId
        ) {
          // Use Redux scheduled time if it matches cart store
          finalScheduledTime = reduxScheduledTime;
        }

        dispatch(
          UpdateCartData({
            subTotal: displaySubtotal,
            typeAddress,
            orderType: orderType,
            storeId: cartStoreId || '',
            apiPricingData: apiPricingData,
            backendTotal: displayTotal,
            backendTax: displayTax,
            backendSubtotal: displaySubtotal,
            backendTotalDiscounts: displayTotalDiscounts,
            backendDeliveryFee: displayServiceCharge,
            scheduledTime: finalScheduledTime,
          })
        );
        navigate('/confirmorder', {
          state: {
            apiPricingData: apiPricingData,
            storeId: cartStoreId || '',
            defaultAdd: defaultAdd || '',
            fromAnonymous: isAnonymous,
            deliveryId: unifiedDeliveryAddressId || '',
            storeAddressDetails: storeaddress,
          },
        });
      }
    } else {
      // For other restaurants, just navigate
      navigate('/confirmorder');
    }
  }, [
    orderType,
    apiError,
    checkoutErr,
    checkoutErrTotal,
    errDelivery,
    cartStoreId,
    navigate,
    apiPricingData,
    isAnonymous,
    displaySubtotal,
    displayTotal,
    displayTax,
    displayTotalDiscounts,
    displayServiceCharge,
    typeAddress,
    defaultAdd,
    storeaddress,
    scheduledTime,
    unifiedDeliveryAddressId,
    dispatch,
    auth?.currentUser?.uid,
  ]);

  useEffect(() => {
    if (user?.bagItems && user.bagItems.length > 0) {
      dispatch(setCart(user.bagItems));
    }
  }, [user?.bagItems, dispatch]);

  // Clear note when cart becomes empty
  useEffect(() => {
    if (cart && cart.length === 0 && note) {
      dispatch(UdpateNote(''));
    }
  }, [cart, note]);

  useEffect(() => {
    if (cartData?.orderType) {
      setOrderType(cartData.orderType);
    }
    if (cartData?.typeAddress) {
      setSelectedAddress(cartData.typeAddress);
    }
  }, [cartData]);

  // Sync order type with user preference (same as CartDrawer)
  useEffect(() => {
    const preferred = user?.pickupOrDelivery;
    if (preferred && (preferred === 'delivery' || preferred === 'pickup')) {
      if (preferred !== orderType) {
        setOrderType(preferred);
      }
    }
  }, [user?.pickupOrDelivery, orderType]);

  // Get store data when stores/cartStoreId changes
  useEffect(() => {
    if (stores && cartStoreId) {
      getUser(auth?.currentUser?.uid);
    }
  }, [stores, cartStoreId]);

  // Get user addresses when user/auth changes
  useEffect(() => {
    if (auth?.currentUser?.uid && user) {
      getUserAddresses(auth?.currentUser?.uid);
    }
  }, [auth?.currentUser?.uid, user]);

  // SYNC: Update local state when delivery address changes from another component (e.g., ChangeBranchMenu)
  // This ensures Cart always shows the current delivery address
  useEffect(() => {
    if (currentDeliveryAddressData && orderType === 'delivery') {
      const newDisplayName =
        currentDeliveryAddressData.displayName ||
        currentDeliveryAddressData.address ||
        '';

      setDefaultAdd(newDisplayName);
      setTypeAddress(currentDeliveryAddressData.nickName || 'Default Address');
      setAddressStore(
        currentDeliveryAddressData.delivery_store_id || cartStoreId
      );
      setApiError('');
    }
  }, [
    currentDeliveryAddressData,
    orderType,
    cartStoreId,
    defaultAdd,
    unifiedDeliveryAddressId,
  ]);

  return (
    <Box>
      <SectionHeader
        title="Cart"
        breadcrumbs={[
          { label: 'Back to Menu', path: '/menu', onClick: handleBackToMenu },
          { label: 'Cart', current: true },
        ]}
        fontFamily="Jost"
        showBreadcrumbs={true}
        titleVariant="h2"
        customStyles={{
          title: { fontSize: '40px', fontWeight: '700' },
          container: { minHeight: '250px' },
        }}
      />
      <Box sx={{ pt: 2, px: 2 }}>
        <Button
          variant="outlined"
          onClick={handleBackToMenu}
          disableRipple
          fullWidth
          sx={{
            textTransform: 'capitalize',
            padding: '12px 32px',
            width: 'fit-content',
            boxShadow: 'none',
            fontWeight: 600,
            borderRadius: '43px',
            backgroundColor: 'white',
            color: environment.mainColor,
            border: `2px solid ${environment.mainColor}`,
            fontFamily: 'Jost',
            '&:hover': {
              backgroundColor: '#FFF4F0',
              border: `2px solid ${environment.mainColor}`,
              boxShadow: 'none',
            },
          }}
        >
          Back to Menu
        </Button>
      </Box>

      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Grid container spacing={{ xs: 2, md: 4 }}>
          {/* Cart Table - 3/4 width */}
          <Grid item xs={12} lg={8}>
            <Box
              sx={{
                borderRadius: '8px',
                overflow: 'hidden',
                border: '1px solid #E6E6E6',
              }}
            >
              {/* Cart Items Table */}
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontFamily: 'Jost, sans-serif',
                          fontWeight: 500,
                          fontSize: { xs: '11px', md: '14px' },
                          color: '#808080',
                          textTransform: 'uppercase',
                          padding: { xs: '10px 6px', md: '16px' },
                        }}
                      >
                        Product
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          fontFamily: 'Jost, sans-serif',
                          fontWeight: 500,
                          fontSize: { xs: '11px', md: '14px' },
                          color: '#808080',
                          textTransform: 'uppercase',
                          padding: { xs: '10px 6px', md: '16px' },
                        }}
                      >
                        Price
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          fontFamily: 'Jost, sans-serif',
                          fontWeight: 500,
                          fontSize: { xs: '11px', md: '14px' },
                          color: '#808080',
                          textTransform: 'uppercase',
                          padding: { xs: '10px 6px', md: '16px' },
                        }}
                      >
                        Qty
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          fontFamily: 'Jost, sans-serif',
                          fontWeight: 500,
                          fontSize: { xs: '11px', md: '14px' },
                          color: '#808080',
                          textTransform: 'uppercase',
                          padding: { xs: '10px 6px', md: '16px' },
                        }}
                      >
                        Total
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          fontFamily: 'Jost, sans-serif',
                          fontWeight: 500,
                          fontSize: { xs: '11px', md: '14px' },
                          color: '#808080',
                          textTransform: 'uppercase',
                          padding: { xs: '10px 4px', md: '16px' },
                        }}
                      >
                        {/* empty on mobile, "Actions" on desktop */}
                        <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
                          Actions
                        </Box>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Show loading first if cart exists but no data yet, or if explicitly loading */}
                    {cartLoading ||
                    (cart &&
                      cart.length > 0 &&
                      !apiPricingData?.lineItemData) ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                          <Skeleton
                            variant="rectangular"
                            width="100%"
                            height={60}
                          />
                        </TableCell>
                      </TableRow>
                    ) : // Show items if we have API data
                    filteredLineItems.length > 0 ? (
                      // Use memoized filtered items to prevent unnecessary re-renders
                      filteredLineItems.map((lineItem, index) => (
                        <CartItemRow
                          key={`${lineItem.variationId || 'item'}-${index}`}
                          lineItem={lineItem}
                          cart={cart}
                          index={index}
                          onItemRemoved={() => {
                            // Immediately refresh pricing when item is removed
                            if (callCalculateSquareItemsPriceImmediate) {
                              callCalculateSquareItemsPriceImmediate();
                            }
                          }}
                          onItemUpdated={() => {
                            // Trigger immediate API call after Redux state updates
                            // Use setTimeout to ensure Redux state has updated
                            setTimeout(() => {
                              if (callCalculateSquareItemsPriceImmediate) {
                                callCalculateSquareItemsPriceImmediate();
                              }
                            }, 50);
                          }}
                          onEditItem={handleEditCartItem}
                        />
                      ))
                    ) : (
                      // Show empty cart only if NOT loading AND cart is actually empty
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                          <Typography
                            variant="h6"
                            color="textSecondary"
                            sx={{ fontFamily: 'Jost, sans-serif' }}
                          >
                            Your cart is empty
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Grid>

          {/* Cart Summary - 1/4 width */}
          <Grid item xs={12} lg={4}>
            <Box
              sx={{
                borderRadius: '16px',
                position: { lg: 'sticky' },
                top: { lg: 24 },
                border: '1px solid #E6E6E6',
                padding: '24px',
                backgroundColor: 'white',
              }}
            >
              {/* Order Type Toggle - Same as CartDrawer */}
              {auth?.currentUser && cart?.length > 0 && (
                <>
                  {/* <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      flexDirection: 'row',
                      padding: '20px 0',
                      backgroundColor: 'transparent',
                      mb: 3,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        backgroundColor: '#F5F5F5',
                        borderRadius: '50px',
                        padding: '4px',
                        width: '100%',
                        maxWidth: '400px',
                      }}
                    >
                      {types?.map((value, index) => {
                        const isHidden =
                          (store?.length > 0 && !enableDelivery && value === 'delivery') ||
                          (store?.length > 0 && !enablePickup && value === 'pickup');
                        if (isHidden) return null;
                        const isActive = orderType === value;
                        const isButtonLocked = !!orderTypeLocked && !isActive;
                        return (
                          <Button
                            key={value || index}
                            disableRipple={true}
                            disabled={isButtonLocked}
                            style={{
                              flex: 1,
                              margin: '0 2px',
                              backgroundColor: isActive
                                ? environment.mainColor
                                : 'transparent',
                              textShadow: isActive
                                ? '0 1px 3px rgba(0, 0, 0, 0.3)'
                                : 'none',
                              color: isActive ? 'white' : '#8E8E93',
                              borderRadius: '46px',
                              fontFamily: 'Jost, sans-serif',
                              fontWeight: '600',
                              fontSize: '14px',
                              textTransform: 'capitalize',
                              padding: '4px',
                              minHeight: '44px',
                              boxShadow: 'none',
                              border: 'none',
                              transition: 'all 0.2s ease-in-out',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '8px',
                              opacity: isButtonLocked ? 0.4 : 1,
                              cursor: isButtonLocked ? 'not-allowed' : 'pointer',
                            }}
                            variant={isActive ? 'contained' : 'text'}
                            onClick={() => {
                              if (!isButtonLocked) handleChangeType(value);
                            }}
                          >
                            {typeToShow[index]}
                          </Button>
                        );
                      })}
                    </Box>
                  </Box> */}

                  <Box
                    sx={{
                      width: '100%',
                      padding: '15px 0',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '8px',
                      mb: 3,
                    }}
                  >
                    {/* Icon Section */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        backgroundColor: '#FFF4F0',
                        marginBottom: '8px',
                      }}
                    >
                      {orderType === 'delivery' ? (
                        !apiError ? (
                          <LocalShippingIcon
                            style={{
                              color: environment.mainColor,
                              fontSize: '28px',
                            }}
                          />
                        ) : (
                          <ReportGmailerrorredIcon
                            style={{
                              color: 'red',
                              fontSize: '28px',
                            }}
                          />
                        )
                      ) : (
                        <LocalShippingIcon
                          style={{
                            color: environment.mainColor,
                            fontSize: '28px',
                          }}
                        />
                      )}
                    </Box>

                    {/* Pickup from / Deliver to label */}
                    <Typography
                      sx={{
                        fontFamily: 'Jost, sans-serif',
                        color: '#808080',
                        fontSize: '13px',
                        fontWeight: '500',
                      }}
                    >
                      {orderType === 'delivery' ? 'Deliver to' : 'Pickup from'}
                    </Typography>

                    {/* Address/Store Text */}
                    <Box sx={{ textAlign: 'center' }}>
                      {orderType === 'delivery' ? (
                        <Typography
                          variant="body1"
                          sx={{
                            fontFamily: 'Jost, sans-serif',
                            color: '#172B4D',
                            fontWeight: '600',
                            fontSize: '16px',
                            lineHeight: '1.4',
                            maxWidth: '280px',
                          }}
                        >
                          {apiError
                            ? t('basket.addressOutService')
                            : defaultAdd || err}
                        </Typography>
                      ) : (
                        <Stack spacing={0.5}>
                          <Typography
                            variant="body1"
                            sx={{
                              fontFamily: 'Jost, sans-serif',
                              color: '#172B4D',
                              fontWeight: '600',
                              fontSize: '16px',
                            }}
                          >
                            {store}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: 'Jost, sans-serif',
                              color: '#172B4D',
                              fontSize: '14px',
                              fontWeight: '600',
                            }}
                          >
                            {storeaddress}
                          </Typography>
                        </Stack>
                      )}
                    </Box>

                    {/* Change Button */}
                    {orderType === 'delivery' && (
                      <Stack spacing={0.5}>
                        <Typography
                          variant="body1"
                          sx={{
                            fontFamily: 'Jost, sans-serif',
                            color: '#172B4D',
                            fontWeight: '600',
                            fontSize: '16px',
                          }}
                        >
                          {store}
                        </Typography>
                        <Typography
                          component="button"
                          onClick={() => {
                            handleClickOpen();
                            setCheckoutErr('');
                          }}
                          sx={{
                            fontFamily: 'Jost, sans-serif',
                            color: environment.mainColor,
                            fontWeight: '600',
                            fontSize: '14px',
                            cursor: 'pointer',
                            backgroundColor: 'transparent',
                            border: 'none',
                            '&:hover': {
                              opacity: 0.8,
                            },
                          }}
                        >
                          {defaultAdd
                            ? `${t('basket.changeAddress')}`
                            : `${t('basket.changeAddress')}`}
                        </Typography>
                      </Stack>
                    )}
                  </Box>
                </>
              )}

              {/* Notes Section */}
              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Special Instructions"
                  value={localNote}
                  inputProps={{
                    autoComplete: 'off',
                    autoCorrect: 'off',
                    autoCapitalize: 'off',
                    spellCheck: 'false',
                    inputMode: 'text',
                  }}
                  onFocus={() => {
                    isEditingNoteRef.current = true;
                  }}
                  onBlur={() => {
                    isEditingNoteRef.current = false;
                    dispatch(UdpateNote(localNote));
                  }}
                  onChange={(e) => setLocalNote(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '16px',
                      fontFamily: 'Jost, sans-serif',
                      fontSize: '16px',
                      '& fieldset': {
                        borderColor: '#E6E6E6',
                      },
                      '&:hover fieldset': {
                        borderColor: '#E6E6E6',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: environment.mainColor,
                      },
                    },
                    '& .MuiInputBase-input': {
                      // Optimize for mobile keyboard performance
                      WebkitAppearance: 'none',
                      '&::placeholder': {
                        fontFamily: 'Jost, sans-serif',
                        color: '#B0B0B0',
                        fontSize: '16px',
                        opacity: 1,
                      },
                    },
                    '& .MuiInputLabel-root': {
                      fontFamily: 'Jost, sans-serif',
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: environment.mainColor,
                    },
                  }}
                />
              </Box>

              {/* Coupon Section */}

              {/* Cart Summary using BillDetails component */}
              <BillDetails
                type={orderType || 'pickup'}
                data={dataForBillDetails}
                total={total}
                addressStore={addressStore}
                store={storeData?.businessName || store || ''}
                dataStore={storeData}
                deliveryFee={displayServiceCharge}
                backendTax={displayTax}
                displayTotal={displayTotal}
                displaySubtotal={displaySubtotal}
                displayModifiersPrice={displayModifiersPrice}
                isCalculatingPrice={isCalculatingPrice}
                displayDeliveryFee={displayServiceCharge}
                totalDiscounts={displayTotalDiscounts}
                storeData={storeData}
                taxesText={apiPricingData?.taxes_text}
                displayOnlineFee={displayOnlineFee}
              />
              {/* Add More Items Button */}
              <Button
                variant="outlined"
                onClick={handleBackToMenu}
                disableRipple
                sx={{
                  marginTop: '20px',
                  textTransform: 'capitalize',
                  padding: '14px 40px',
                  width: '100%',
                  boxShadow: 'none',
                  fontWeight: 600,
                  borderRadius: '43px',
                  backgroundColor: 'white',
                  color: environment.mainColor,
                  border: `2px solid ${environment.mainColor}`,
                  fontFamily: 'Jost',
                  mb: 2,
                  '&:hover': {
                    backgroundColor: '#FFF4F0',
                    border: `2px solid ${environment.mainColor}`,
                    boxShadow: 'none',
                  },
                }}
              >
                + Add More Items
              </Button>

              {/* Checkout Button */}
              <Button
                variant="contained"
                disabled={
                  (orderType === 'delivery' && apiError !== '') ||
                  checkoutErr !== '' ||
                  checkoutErrTotal !== '' ||
                  errDelivery !== '' ||
                  !cart ||
                  cart.length === 0
                }
                onClick={handleCheckout}
                disableRipple
                sx={{
                  textTransform: 'capitalize',
                  padding: '16px 40px',
                  width: '100%',
                  boxShadow: 'none',
                  fontWeight: 600,
                  borderRadius: '43px',
                  backgroundColor: environment.mainColor,
                  textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
                  fontFamily: 'Jost',
                  '&:hover': {
                    backgroundColor: environment.mainColor,
                    boxShadow: 'none',
                  },
                  '&:disabled': {
                    backgroundColor: '#ccc',
                    color: '#999',
                  },
                }}
              >
                {t('basket.procedToCheckout')}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Container>
      <NeedQuestion />

      {/* Address Selection Modal - Same as CartDrawer */}
      {orderType === 'delivery' || forceDeliveryDialog ? (
        <DeliveryDialog
          open={open}
          handleDefault={setDefaultAdd}
          handleCloseCartDialoge={() => {
            // No-op for Cart page - drawer close not applicable
          }}
          token={user?.uid}
          setErr={setErr}
          handleClose={() => {
            handleClose();
            setForceDeliveryDialog(false);
          }}
          items={addresses}
          setAddressStore={setAddressStore}
          handleFee={setDeliveryFee}
          setApiError={setApiError}
          currentStore={storeData}
          onAddressChange={
            forceDeliveryDialog ? handleNewAddressAdded : handleChangeAddress
          }
        />
      ) : null}

      {(environment?.restaurantId === 'fattyPattyDev' ||
        environment?.restaurantId === 'fattyPatty') &&
      editDialogItem ? (
        <FattyPattyDialog
          open={editDialogOpen}
          handleClose={() => {
            setEditDialogOpen(false);
            setEditDialogItem(null);
            setEditCartItemPayload(null);
          }}
          item={editDialogItem}
          handleOpen={() => {}}
          modifiers={modifiers}
          storeId={cartStoreId}
          editCartItem={editCartItemPayload}
        />
      ) : null}

      <Dialog
        open={deliveryTooFarDialog}
        onClose={() => setDeliveryTooFarDialog(false)}
      >
        <DialogTitle sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 700 }}>
          Delivery Not Available
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: 'Jost, sans-serif', color: '#172B4D' }}>
            Delivery is not available for this address. Your delivery address is
            more than 5 miles from the store. If you want to continue, please
            choose another address.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: '16px', gap: 1 }}>
          <Button
            onClick={() => {
              setDeliveryTooFarDialog(false);
              setForceDeliveryDialog(true);
              handleClickOpen();
            }}
            variant="contained"
            disableRipple
            sx={{
              backgroundColor: environment.mainColor,
              color: 'white',
              borderRadius: '43px',
              fontFamily: 'Jost',
              textTransform: 'capitalize',
              boxShadow: 'none',
              '&:hover': { backgroundColor: environment.mainColor, boxShadow: 'none' },
            }}
          >
            Choose Another Address
          </Button>
          <Button
            onClick={() => setDeliveryTooFarDialog(false)}
            variant="outlined"
            disableRipple
            sx={{
              borderRadius: '43px',
              fontFamily: 'Jost',
              textTransform: 'capitalize',
              borderColor: '#E6E6E6',
              color: '#172B4D',
              '&:hover': { borderColor: '#E6E6E6', backgroundColor: '#f5f5f5' },
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Cart;
